import React, { useState, useEffect } from "react";
import { Autocomplete, Box, Card, CardContent, Typography, Button, Divider, MenuItem, TextField, Checkbox, FormControlLabel } from "@mui/material";
import Cookies from "js-cookie";
import { useIntl } from "react-intl";
import { getUserAddresses } from "../../../hooks/Sessions/Addresses";
import {
    EMAIL_MAX_LENGTH,
    isValidEmail,
    isValidPhone,
    sanitizePhone,
} from "../../../utils/validators";
import PhoneField from "../../../components/PhoneField/PhoneField";

const API_URL = process.env.REACT_APP_API_URL;

// Same normalization as backend utils/postalCodes.normalizePlace.
const normalizePlace = (name) =>
    String(name || "")
        .toLowerCase()
        .replace(/ł/g, "l")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[\s-]+/g, " ")
        .trim();

const defaultCountry = "Polska";

const validateName = (value) => /^[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+$/.test(value);
const validateNIP = (value) => /^\d{10}$/.test(value);
const validatePostalCode = (value) => /^\d{2}-\d{3}$/.test(value);

const defaultForm = {
    email: "",
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    country: defaultCountry,
    phone: "+48",
    isCompany: false, // Show/hide company/nip
    company: "",
    nip: "",
};

const fieldSx = {
    "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "text.primary" },
    "& label.Mui-focused": { color: "text.primary" },
};
const half = { flexBasis: { xs: "100%", sm: "calc(50% - 8px)" }, flexGrow: 1, ...fieldSx };

const ShippingStep = ({
    formId,
    shippingData,
    setShippingData,
    onBack,
    onNext,
    isGuest
}) => {
    const intl = useIntl();
    const t = (id, values) => intl.formatMessage({ id }, values);
    const [form, setForm] = useState({ ...defaultForm, ...shippingData });
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState("");
    const [errors, setErrors] = useState({});
    // Places for the typed postal code: null = not looked up (yet), [] = code
    // not in our dataset (the backend then accepts any real Polish place).
    const [places, setPlaces] = useState(null);
    const [checking, setChecking] = useState(false);

    useEffect(() => {
        if (!/^\d{2}-\d{3}$/.test(form.zip)) {
            setPlaces(null);
            return undefined;
        }
        let cancelled = false;
        fetch(`${API_URL}/postal-codes/${form.zip}`)
            .then((res) => (res.ok ? res.json() : null))
            .then((json) => {
                if (cancelled || !json) return;
                const list = json.data?.places || [];
                setPlaces(list);
                // One place for the code: fill the city in for the shopper.
                if (list.length === 1) {
                    setForm((prev) => (prev.city ? prev : { ...prev, city: list[0] }));
                }
            })
            .catch(() => !cancelled && setPlaces(null));
        return () => {
            cancelled = true;
        };
    }, [form.zip]);

    useEffect(() => {
        async function fetchAddresses() {
            const userStr = Cookies.get("user");
            const token = Cookies.get("token");
            const user = userStr ? JSON.parse(userStr) : null;

            // Only fetch addresses for authenticated users
            if (user?.id && token && !isGuest) {
                try {
                    const response = await getUserAddresses(user.id);
                    setAddresses(response.data || []);
                    const defaultAddr = response.data?.find((addr) => addr.isDefault);
                    if (defaultAddr && !selectedAddressId) {
                        fillFormWithAddress(defaultAddr);
                        setSelectedAddressId(defaultAddr.id);
                    }
                } catch (err) {
                    setAddresses([]);
                }
            }
        }
        fetchAddresses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGuest]);

    // Fill form fields with selected address
    const fillFormWithAddress = (addr) => {
        setForm((prev) => ({
            ...prev,
            email: addr.email || prev.email,
            firstName: addr.firstName || "",
            lastName: addr.lastName || "",
            address: addr.address || "",
            zip: addr.zip || "",
            city: addr.city || "",
            country: defaultCountry,
            phone: addr.phone ? sanitizePhone(addr.phone) : "+48",
            // Company fields only if present
            isCompany: !!addr.nip,
            company: addr.company || "",
            nip: addr.nip || "",
        }));
        setErrors({});
    };

    // Address select dropdown handler
    const handleAddressSelect = (e) => {
        const selectedId = e.target.value;
        setSelectedAddressId(selectedId);
        const addr = addresses.find((a) => a.id === selectedId);
        if (addr) {
            fillFormWithAddress(addr);
        } else {
            setForm({ ...defaultForm, email: form.email });
        }
    };

    // Handle field input
    const handleInput = (e) => {
        let { name, value, type, checked } = e.target;
        if (name === "zip") {
            value = value.replace(/[^\d]/g, "");
            if (value.length > 2) value = `${value.slice(0, 2)}-${value.slice(2, 5)}`;
            if (value.length > 6) value = value.slice(0, 6);
        }

        if (name === "email") value = value.replace(/\s/g, "").slice(0, EMAIL_MAX_LENGTH);

        if (name === "nip") {
            value = value.replace(/[^\d]/g, "");
            if (value.length > 10) value = value.slice(0, 10);
        }

        if (["firstName", "lastName"].includes(name)) {
            value = value.replace(/[^A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]/g, "");
        }

        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    // Form validation
    const validateForm = () => {
        let errs = {};
        if (!isValidEmail(form.email)) errs.email = t("error_email");
        if (!validateName(form.firstName)) errs.firstName = t("error_first_name");
        if (!validateName(form.lastName)) errs.lastName = t("error_last_name");
        if (!form.address.trim()) errs.address = t("error_required");
        if (!form.city.trim()) errs.city = t("error_required");
        if (!validatePostalCode(form.zip)) errs.zip = t("error_zip");
        else if (!errs.city && places?.length && !places.some((p) => normalizePlace(p) === normalizePlace(form.city))) {
            errs.city = t("error_city_mismatch", { zip: form.zip });
        }
        if (!isValidPhone(form.phone)) errs.phone = t("error_phone");
        if (form.isCompany) {
            if (!form.company.trim()) errs.company = t("error_required");
            if (!validateNIP(form.nip)) errs.nip = t("error_nip");
        }
        return errs;
    };

    // Also reached from the summary panel button (it submits this form by id).
    // For codes missing from the dataset, ask the backend whether the city
    // is a real place (same rule as POST /orders). Network errors don't block.
    const remoteCityError = async () => {
        if (places === null || places.length > 0) return null;
        try {
            const res = await fetch(`${API_URL}/postal-codes/${form.zip}?city=${encodeURIComponent(form.city.trim())}`);
            const json = res.ok ? await res.json() : null;
            return json?.data?.cityError ? t("error_city_unknown") : null;
        } catch {
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (checking) return;
        const currentErrors = validateForm();
        if (!currentErrors.city && !currentErrors.zip) {
            setChecking(true);
            const cityError = await remoteCityError();
            setChecking(false);
            if (cityError) currentErrors.city = cityError;
        }
        setErrors(currentErrors);
        if (Object.keys(currentErrors).length > 0) {
            const first = Object.keys(currentErrors)[0];
            document.querySelector(`#${formId} [name="${first}"]`)?.focus();
            return;
        }
        setShippingData({ ...form, country: defaultCountry });
        onNext();
    };

    const field = (name, labelId, extra = {}) => (
        <TextField
            required
            name={name}
            label={t(labelId)}
            fullWidth
            value={form[name]}
            onChange={handleInput}
            error={!!errors[name]}
            helperText={errors[name]}
            sx={half}
            {...extra}
        />
    );

    return (
        <Box component="form" id={formId} onSubmit={handleSubmit} noValidate>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>{t("shipping_details_heading")}</Typography>

                    {/* Saved Addresses Dropdown */}
                    {addresses.length > 0 && (
                        <TextField
                            select
                            label={t("saved_address_select")}
                            value={selectedAddressId}
                            onChange={handleAddressSelect}
                            fullWidth
                            margin="normal"
                            size="small"
                        >
                            <MenuItem value="">
                                <em>{t("none")}</em>
                            </MenuItem>
                            {addresses?.map((addr) => (
                                <MenuItem key={addr.id} value={addr.id}>
                                    {`${addr.firstName} ${addr.lastName} - ${addr.address}, ${addr.city}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        fullWidth
                        required
                        type="email"
                        label={t("email_placeholder")}
                        name="email"
                        margin="normal"
                        autoComplete="email"
                        value={form.email}
                        onChange={handleInput}
                        error={!!errors.email}
                        helperText={errors.email}
                        inputProps={{ maxLength: EMAIL_MAX_LENGTH, inputMode: "email" }}
                        sx={fieldSx}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {field("firstName", "first_name", { autoComplete: "given-name" })}
                        {field("lastName", "last_name", { autoComplete: "family-name" })}
                        {field("address", "address", { autoComplete: "street-address" })}
                        {field("zip", "postal_code", {
                            placeholder: "00-000",
                            autoComplete: "postal-code",
                            inputProps: { inputMode: "numeric", maxLength: 6 },
                        })}
                        <Autocomplete
                            freeSolo
                            options={places || []}
                            inputValue={form.city}
                            onInputChange={(_, value, reason) => {
                                if (reason === "reset" && !value) return;
                                handleInput({ target: { name: "city", value } });
                            }}
                            sx={half}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    name="city"
                                    label={t("city")}
                                    autoComplete="address-level2"
                                    error={!!errors.city}
                                    helperText={errors.city || (places?.length > 1 ? t("city_pick_hint") : "")}
                                />
                            )}
                        />
                        {/* We only ship within Poland. */}
                        <TextField
                            name="country"
                            label={t("country")}
                            value={t("shipping_info_country")}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            helperText={t("shipping_poland_only_hint")}
                            sx={half}
                        />
                        <PhoneField
                            key={selectedAddressId || "manual"}
                            value={form.phone}
                            onChange={(phone) => {
                                setForm((prev) => ({ ...prev, phone }));
                                if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
                            }}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            sx={{ flexBasis: "100%" }}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Checkbox for invoice */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.isCompany}
                                onChange={handleInput}
                                name="isCompany"
                            />
                        }
                        label={t("want_invoice")}
                        sx={{ mt: 1 }}
                    />

                    {form.isCompany &&
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                            {field("company", "company_name", { sx: { flexBasis: { xs: "100%", sm: "60%" }, flexGrow: 1, ...fieldSx } })}
                            {field("nip", "nip", {
                                inputProps: { inputMode: "numeric", maxLength: 10 },
                                sx: { flexBasis: { xs: "100%", sm: "30%" }, flexGrow: 1, ...fieldSx },
                            })}
                        </Box>
                    }
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onBack}
                            sx={{ color: "text.primary", borderColor: "text.primary" }}
                        >
                            {t("back")}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={checking}
                            sx={{
                                flexBasis: { xs: "60%", sm: "33%" },
                                bgcolor: "text.primary",
                                color: "background.default",
                                "&:hover": { bgcolor: "text.secondary" },
                            }}
                        >
                            {t("next")}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ShippingStep;
